/**
 * Sub-nav for Research Amplifier (spec: 8 steps).
 * Overview → Source Capture → Quote Extractor → Data Miner → Credibility Scoring →
 * Insight Distiller → Pattern Finder → Export to Argument / Copy Bank
 *
 * Mobile (<md): collapses to a native <select> dropdown — no horizontal scrolling.
 * Desktop (md+): full horizontal tab row.
 */

import { FC } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { BookOpen, CheckCircle2, ChevronDown, FileInput, Quote, BarChart3, Lightbulb, GitBranch, Share2 } from 'lucide-react'

const ITEMS: { id: string; label: string; path: string; icon: typeof BookOpen }[] = [
    { id: 'overview', label: 'Overview', path: '/research', icon: BookOpen },
    { id: 'source-capture', label: 'Source Capture', path: '/research/source-capture', icon: FileInput },
    { id: 'quote-extractor', label: 'Quote Extractor', path: '/research/quote-extractor', icon: Quote },
    { id: 'data-miner', label: 'Data Miner', path: '/research/data-miner', icon: BarChart3 },
    { id: 'credibility-scoring', label: 'Credibility Scoring', path: '/research/credibility-scoring', icon: BarChart3 },
    { id: 'insight-distiller', label: 'Insight Distiller', path: '/research/insight-distiller', icon: Lightbulb },
    { id: 'pattern-finder', label: 'Pattern Finder', path: '/research/pattern-finder', icon: GitBranch },
    { id: 'export', label: 'Export', path: '/research/export', icon: Share2 },
]

export const ResearchAmplifierSubNav: FC = () => {
    const navigate = useNavigate()
    const { runId } = useParams<{ runId: string }>()
    const location = useLocation()
    const base = `/dashboard/persuasion-lab/${runId || ''}`

    const currentId = (() => {
        const path = location.pathname
        for (const item of ITEMS) {
            if (path === base + item.path) return item.id
        }
        if (path.includes('/research')) return 'overview'
        return 'overview'
    })()

    if (!runId) return null

    const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const item = ITEMS.find(i => i.id === e.target.value)
        if (item) navigate(`${base}${item.path}`)
    }

    return (
        /* sticky top-[120px] = app outer header (64px) + CampaignPageHeader (~56px) */
        <div className='bg-white border-b border-gray-200 sticky top-[120px] z-20 shadow-sm'>
            <div className='max-w-7xl mx-auto px-4 sm:px-6'>

                {/* Mobile: native select dropdown */}
                <div className='md:hidden py-2'>
                    <div className='relative'>
                        <select
                            value={currentId}
                            onChange={handleSelectChange}
                            className='w-full appearance-none rounded-lg border border-gray-200 bg-gray-50 pl-3 pr-8 py-2 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500'
                        >
                            {ITEMS.map(item => (
                                <option key={item.id} value={item.id}>{item.label}</option>
                            ))}
                        </select>
                        <ChevronDown className='pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400' />
                    </div>
                </div>

                {/* Desktop: full horizontal tab row */}
                <nav className='hidden md:flex items-center gap-1 py-2'>
                    {ITEMS.map((item) => {
                        const isActive = currentId === item.id
                        const href = `${base}${item.path}`
                        const Icon = item.icon
                        return (
                            <button
                                key={item.id}
                                type='button'
                                onClick={() => navigate(href)}
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                                    isActive ? 'bg-violet-100 text-violet-900 shadow-sm' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                                }`}
                            >
                                <Icon className={`w-4 h-4 ${isActive ? 'text-violet-600' : ''}`} />
                                <span>{item.label}</span>
                                {isActive && <CheckCircle2 className='w-3.5 h-3.5 text-violet-600' />}
                            </button>
                        )
                    })}
                </nav>

            </div>
        </div>
    )
}
